import * as React from 'react';
import '../../Css/Global/Global.css';
import InfoCard from './InfoCard';
import { Grid, Snackbar, Typography } from "@mui/material";
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BalanceIcon from '@mui/icons-material/Balance';
import CountUp from 'react-countup';
import MuiAlert from '@mui/material/Alert';
import TableComponent from '../../Components/TableComponent';
import CardSection from '../../Components/CardSection';
import SpedStatusHandler from './SpedStatusHandler';
import { getSafeNumber } from '../../Helpers';
import { getToken as getAuthCookieToken } from '../../Storages/cookies';


const Dashboard: React.FC = () => {

    const [loading, setLoading] = React.useState(true);
    const [nenhumSped, setNenhumSped] = React.useState(false);
    const [analisando, setAnalisando] = React.useState(false);
    const [totalProdutos, setTotalProdutos] = React.useState<number | null>(null);
    const [totalNfesEntrada, setTotalNfesEntrada] = React.useState<number | null>(null);
    const [totalNfesSaida, setTotalNfesSaida] = React.useState<number | null>(null);
    const [totalParticipantes, setTotalParticipantes] = React.useState<number | null>(null);
    const [showSnackbar, setShowSnackbar] = React.useState(false);
    const [totalVendido, setTotalVendido] = React.useState<number | null>(null);
    const [totalComprado, setTotalComprado] = React.useState<number | null>(null);
    const [ticketMedio, setTicketMedio] = React.useState<number | null>(null);

    const [totalIcms, setTotalIcms] = React.useState<number | null>(null);
    const [ajustesIcmsLista, setAjustesIcmsLista] = React.useState([]);
    const [ajustesIcmsTotalCredito, setAjustesIcmsTotalCredito] =  React.useState<number | null>(null);
    const [ajustesIcmsTotalDebito, setAjustesIcmsTotalDebito] =  React.useState<number | null>(null);

    const [totalIpi, setTotalIpi] = React.useState<number | null>(null);
    const [totalIcmsSt, setTotalIcmsSt] = React.useState<number | null>(null);
    const [ajustesIcmsStLista, setAjustesIcmsStLista] = React.useState([]);
    const [ajustesIcmsStTotalCredito, setAjustesIcmsStTotalCredito] =  React.useState<number | null>(null);
    const [ajustesIcmsStTotalDebito, setAjustesIcmsStTotalDebito] =  React.useState<number | null>(null);
    
    const [spedFiscalIdSelecionado, setSpedFiscalIdSelecionado] = React.useState<number | null>(null);

    // ---------------------------
    // Funções utilitárias de fetch
    // ---------------------------
    const BASE_URL = process.env.REACT_APP_API_URL ?? '';

    const buildAuthHeaders = () => {
        const token =
            getAuthCookieToken() ||
            localStorage.getItem('token') ||
            sessionStorage.getItem('token') ||
            '';
        const headers: Record<string, string> = { 'Accept': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return headers;
    };

    const fetchJson = async (url: string) => {
        const res = await fetch(url, {
            headers: buildAuthHeaders(),
            credentials: 'include'
        });
        return res.json();
    };

    const getUltimoSpedStatus = async (id?: number | null) => {
        const url = (typeof id === 'number' && id > 0)
            ? `${BASE_URL}sped-fiscal/ultimo/${id}`
            : `${BASE_URL}sped-fiscal/ultimo`;
        return fetchJson(url);
    };

    // Totais / métricas do dashboard
    const getTotalProdutos = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/total-produtos/${id}`;
        const data = await fetchJson(url);
        const value = getSafeNumber(data, 'total');
        return value;
    };

    const getTotalNfes = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/total-nfes/${id}`;
        const data = await fetchJson(url);
        const value = {
            entrada: getSafeNumber(data, 'total_entrada'),
            saida: getSafeNumber(data, 'total_saida'),
        };
        return value;
    };

    const getTotalParticipantes = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/total-participantes/${id}`;
        const data = await fetchJson(url);
        const value = getSafeNumber(data, 'total');
        return value;
    };

    const getTotalVendas = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/total-vendas/${id}`;
        const data = await fetchJson(url);
        const value = getSafeNumber(data, 'total');
        return value;
    };

    const getTotalCompras = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/total-compras/${id}`;
        const data = await fetchJson(url);
        const value = getSafeNumber(data, 'total');
        return value;
    };

    const getTicketMedio = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/ticket-medio/${id}`;
        const data = await fetchJson(url);
        const value = getSafeNumber(data, 'total');
        return value;
    };

    const getTotalIcms = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/total-icms/${id}`;
        const data = await fetchJson(url);
        const value = getSafeNumber(data, 'total');
        return value;
    };

    const getAjustesIcms = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/getAjustesIcms/${id}`;
        return fetchJson(url); // { ajustes, totais: { credito, debito } }
    };

    const getTotalIpi = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/total-ipi/${id}`;
        const data = await fetchJson(url);
        const value = getSafeNumber(data, 'total');
        return value;
    };

    const getTotalIcmsSt = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/total-icms-st/${id}`;
        const data = await fetchJson(url);
        const value = getSafeNumber(data, 'total');
        return value;
    };

    const getAjustesIcmsSt = async (id: number) => {
        const url = `${BASE_URL}sped-fiscal/getAjustesIcmsSt/${id}`;
        return fetchJson(url); // { ajustes, totais: { credito, debito } }
    };

    React.useEffect(() => {

        (async () => {
            try {
                const sped = await getUltimoSpedStatus(spedFiscalIdSelecionado);

                if (sped.status === 'Realizando importação') {
                    setAnalisando(true);
                } else if (sped.status === 'Nenhum SPED Fiscal importado ainda') {
                    setNenhumSped(true);
                } else {
                    setAnalisando(false);
                }
            } catch (e) {
                console.error('Erro ao carregar dados do dashboard', e);
            } finally {
                // Evita "flicker" de loading quando ainda vamos buscar os demais dados
                if (!spedFiscalIdSelecionado) {
                    setLoading(false);
                }
            }
        })()


        if (!spedFiscalIdSelecionado) return;
        
        const loadDashboardData = async (id: number) => {
            try {
                setLoading(true);

                const [
                    totalProdutosData,
                    nfesData,
                    totalParticipantesData,
                    totalVendasData,
                    totalComprasData,
                    ticketMedioData,
                    totalIcmsData,
                    ajustesIcmsData,
                    totalIpiData,
                    totalIcmsStData,
                    ajustesIcmsStData
                ] = await Promise.all([
                    getTotalProdutos(id),
                    getTotalNfes(id),
                    getTotalParticipantes(id),
                    getTotalVendas(id),
                    getTotalCompras(id),
                    getTicketMedio(id),
                    getTotalIcms(id),
                    getAjustesIcms(id),
                    getTotalIpi(id),
                    getTotalIcmsSt(id),
                    getAjustesIcmsSt(id)
                ]);

                // Logs diagnósticos para conferir o SPED e os totais retornados
                console.log('[Dashboard] carregado SPED', id, {
                    nfes: nfesData,
                    totalVendas: totalVendasData,
                    totalCompras: totalComprasData,
                    totalProdutos: totalProdutosData,
                    totalParticipantes: totalParticipantesData,
                    ticketMedio: ticketMedioData,
                    totalIcms: totalIcmsData,
                    totalIpi: totalIpiData,
                    totalIcmsSt: totalIcmsStData
                });

                setTotalProdutos(totalProdutosData);
                setTotalNfesEntrada(nfesData.entrada);
                setTotalNfesSaida(nfesData.saida);
                setTotalParticipantes(totalParticipantesData);
                setTotalVendido(totalVendasData);
                setTotalComprado(totalComprasData);
                setTicketMedio(ticketMedioData);
                setTotalIcms(totalIcmsData);

                setAjustesIcmsLista(ajustesIcmsData.ajustes);
                setAjustesIcmsTotalCredito(ajustesIcmsData.totais.credito);
                setAjustesIcmsTotalDebito(ajustesIcmsData.totais.debito);

                setTotalIpi(totalIpiData);
                setTotalIcmsSt(totalIcmsStData);

                setAjustesIcmsStLista(ajustesIcmsStData.ajustes);
                setAjustesIcmsStTotalCredito(ajustesIcmsStData.totais.credito);
                setAjustesIcmsStTotalDebito(ajustesIcmsStData.totais.debito);
            } catch (e) {
                console.error('Erro ao carregar dados do dashboard', e);
            } finally {
                setLoading(false);
            }
        };

        loadDashboardData(spedFiscalIdSelecionado);
    }, [spedFiscalIdSelecionado]);

    return (
        <>
          <Grid container spacing={1} className="grid-container">
                <SpedStatusHandler
                    onSpedFiscalIdChange={(id: number | null) => {
                        setSpedFiscalIdSelecionado(id);
                    }}
                    isDashboard={true}
                />

                {/* SEÇÃO 1: Totais do SPED Fiscal (CardSection) */}
                <CardSection title="Totais do SPED Fiscal" icon={<MonetizationOnIcon />} iconColor="info">

                <InfoCard
                    title="NFEs"
                    title1="Entradas"
                    title2="Saídas"
                    title1Color="error.main"
                    title2Color="success.main"
                    value={<CountUp end={totalNfesEntrada || 0} duration={0.5} separator="." preserveValue />}
                    value2={<CountUp end={totalNfesSaida || 0} duration={0.5} separator="." preserveValue />}
                    size={3}
                    loading={loading}
                />
                <InfoCard
                    title="Participantes"
                    value={<CountUp end={totalParticipantes || 0} duration={0.5} separator="." preserveValue />}
                    size={3}
                    hideTitle={false}
                    loading={loading}
                />
                
                <InfoCard
                    title="Produtos"
                    value={<CountUp end={totalProdutos || 0} duration={0.5} separator="." preserveValue />}
                    size={3}
                    hideTitle={false}
                    loading={loading}
                />

                <InfoCard
                    title="Ticket Médio"
                    value={
                        <CountUp
                            end={ticketMedio || 0}
                            duration={0.5}
                            separator="."
                            decimals={2}
                            decimal=","
                            prefix="R$ "
                            preserveValue
                        />
                    }
                    size={3}
                    hideTitle={false}
                    loading={loading}
                />
                
                <InfoCard
                    title="Total de Entradas"
                    value={totalComprado ?? 0}
                    formatAsCurrency={true}
                    size={6}
                    hideTitle={false}
                    loading={loading}
                />
                <InfoCard
                    title="Total de Saídas"
                    value={totalVendido ?? 0}
                    formatAsCurrency={true}
                    size={6}
                    hideTitle={false}
                    loading={loading}
                />
                </CardSection>
                
                {/* SEÇÃO 2: Apuração de Tributos (CardSection) */}
                <CardSection title="Apuração de Tributos" icon={<BalanceIcon />} iconColor="info">

                <InfoCard
                    title="Ajustes do ICMS"
                    title1="DÉBITO"
                    title2="CRÉDITO"
                    title1Color="error.main"
                    title2Color="success.main"
                    value={
                        <CountUp
                            end={ajustesIcmsTotalDebito || 0}
                            duration={0.5}
                            separator="."
                            decimals={2}
                            decimal=","
                            prefix="R$ "
                            preserveValue
                        />
                    }
                    value2={
                        <CountUp
                            end={ajustesIcmsTotalCredito || 0}
                            duration={0.5}
                            separator="."
                            decimals={2}
                            decimal=","
                            prefix="R$ "
                            preserveValue
                        />
                    }
                    size={6}
                    formatAsCurrency={true}
                    hideTitle={false}
                    loading={loading}
                />
                <InfoCard
                    title="ICMS a Recolher"
                    value={totalIcms ?? 0}
                    formatAsCurrency={true}
                    size={6}
                    hideTitle={false}
                    loading={loading}
                />
                
                <TableComponent
                    title="Detalhes do Ajuste no ICMS"
                    value={ajustesIcmsLista}
                    loading={loading} />
                </CardSection>
                
                {/* SEÇÃO 3: ICMS ST e IPI (CardSection) */}
                <CardSection title="ICMS ST e IPI" icon={<BalanceIcon />} iconColor="info">
                  <InfoCard
                      title="Ajustes do ICMS ST"
                      title1="DÉBITO"
                      title2="CRÉDITO"
                      title1Color="error.main"
                      title2Color="success.main"
                      value={
                          <CountUp
                              end={ajustesIcmsStTotalDebito || 0}
                              duration={0.5}
                              separator="."
                              decimals={2}
                              decimal=","
                              prefix="R$ "
                              preserveValue
                          />
                      }
                      value2={
                          <CountUp
                              end={ajustesIcmsStTotalCredito || 0}
                              duration={0.5}
                              separator="."
                              decimals={2}
                              decimal=","
                              prefix="R$ "
                              preserveValue
                          />
                      }
                      size={6}
                      formatAsCurrency={true}
                      hideTitle={false}
                      loading={loading}
                  />
                  
                  <InfoCard
                      title="ICMS ST a Recolher"
                      value={totalIcmsSt ?? 0}
                      formatAsCurrency={true}
                      size={3}
                      hideTitle={false}
                      loading={loading}
                  />
                  <InfoCard
                      title="IPI"
                      value={totalIpi ?? 0}
                      formatAsCurrency={true}
                      size={3}
                      hideTitle={false}
                      loading={loading}
                  />
                
                  <TableComponent
                    title="Detalhes do Ajuste no ICMS ST"
                    value={ajustesIcmsStLista}
                    loading={loading} />
                  </CardSection>
            </Grid>

          <Snackbar
            open={showSnackbar}
            autoHideDuration={4000}
            onClose={() => setShowSnackbar(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <MuiAlert
              onClose={() => setShowSnackbar(false)}
              severity="success"
              elevation={6}
              variant="filled"
              sx={{ width: '100%' }}
            >
              Análise do SPED Fiscal concluída com sucesso!
            </MuiAlert>
          </Snackbar>
        </>
      );
}


export default Dashboard;

